import { useState } from 'react';
import {
  FileText,
  AlertTriangle,
  Building2,
  Globe,
  Edit2,
  Trash2,
  MoreHorizontal,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { KbEntry } from '@/hooks/useKnowledgeBase';
import { sourceTypeMeta, isFirmSpecific } from '../kbDisplay';

/**
 * Field name a {@link KbEntryTable} can be sorted by.
 *
 * The table renders sortable header chevrons for `question_text`,
 * `category`, `source_type`, and `updated_at`. `created_at` is part of
 * the type union and is accepted by the `onSort` callback even though
 * the table does not surface a "Created" column header.
 */
export type SortField = 'question_text' | 'category' | 'source_type' | 'created_at' | 'updated_at';
/** Sort direction passed to {@link KbEntryTable} via the `sortDirection` prop. */
export type SortDirection = 'asc' | 'desc';

/**
 * Props for {@link KbEntryTable}.
 *
 * Selection and sorting are both controlled by the parent — the component
 * does not sort or filter `entries` itself. When `onToggleSelect` is
 * omitted, the leading checkbox column is not rendered.
 */
export interface KbEntryTableProps {
  /** KB entries to render, in the order they appear in the table. */
  entries: KbEntry[];
  /** Invoked when the user clicks "Edit" in a row's actions menu. */
  onEdit: (entry: KbEntry) => void;
  /** Invoked when the user clicks "Delete" in a row's actions menu. */
  onDelete: (entry: KbEntry) => void;
  /** Invoked when the user clicks "Mark Stale" on a non-stale entry. */
  onMarkStale: (entry: KbEntry) => void;
  /** Invoked when the user clicks "Mark Current" on a stale entry. */
  onClearStale: (entry: KbEntry) => void;
  /** Optional row-click handler. When set, the question cell gets a pointer cursor and a hover tint. */
  onSelect?: (entry: KbEntry) => void;
  /** Set of entry ids currently selected; drives row tint and checkbox state. */
  selectedIds?: Set<number>;
  /** Toggles a single row's selection. When omitted, the selection checkbox column is not rendered. */
  onToggleSelect?: (entryId: number) => void;
  /** Toggles the header "select all" checkbox. */
  onSelectAll?: () => void;
  /** Field the table is currently sorted by; drives header chevron direction. */
  sortField?: SortField;
  /** Current sort direction; drives header chevron direction. */
  sortDirection?: SortDirection;
  /** Invoked when a sortable column header is clicked. */
  onSort?: (field: SortField) => void;
  /** Extra classes applied to the outer wrapper. */
  className?: string;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function truncateText(text: string | null | undefined, maxLength: number): string {
  // response_text is nullable (migration 007): question-bank entries have no
  // answer yet and must render as an em dash, not crash the table.
  if (!text) return '—';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

interface SortHeaderProps {
  label: string;
  field: SortField;
  currentField?: SortField;
  direction?: SortDirection;
  onSort?: (field: SortField) => void;
}

function SortHeader({ label, field, currentField, direction, onSort }: SortHeaderProps) {
  const isActive = currentField === field;

  return (
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => onSort?.(field)}
    >
      {label}
      {isActive ? (
        direction === 'asc' ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
      )}
    </button>
  );
}

/**
 * Sortable table view of knowledge base entries.
 *
 * Renders one row per entry with columns for question, category, source
 * type, scope (firm vs. global), staleness, last updated, and an actions
 * dropdown (Edit, Mark Stale or Mark Current, Delete). Stale entries get
 * an amber row tint; selected rows get a primary tint.
 *
 * When `entries` is empty an inline "No entries found" placeholder is
 * rendered in place of the table body.
 */
export function KbEntryTable({
  entries,
  onEdit,
  onDelete,
  onMarkStale,
  onClearStale,
  onSelect,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  sortField,
  sortDirection,
  onSort,
  className,
}: KbEntryTableProps) {
  const [openActionId, setOpenActionId] = useState<number | null>(null);

  const allSelected =
    selectedIds && entries.length > 0 && entries.every((e) => selectedIds.has(e.id));
  const someSelected =
    selectedIds && entries.some((e) => selectedIds.has(e.id)) && !allSelected;

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b bg-muted/50">
            {onToggleSelect && (
              <th className="w-10 px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected ?? false;
                  }}
                  onChange={onSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
            )}
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[300px]">
              <SortHeader
                label="Question"
                field="question_text"
                currentField={sortField}
                direction={sortDirection}
                onSort={onSort}
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-32">
              <SortHeader
                label="Category"
                field="category"
                currentField={sortField}
                direction={sortDirection}
                onSort={onSort}
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-24">
              <SortHeader
                label="Source"
                field="source_type"
                currentField={sortField}
                direction={sortDirection}
                onSort={onSort}
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-24">
              Scope
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">
              Status
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-28">
              <SortHeader
                label="Updated"
                field="updated_at"
                currentField={sortField}
                direction={sortDirection}
                onSort={onSort}
              />
            </th>
            <th className="px-3 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {entries.map((entry) => {
            const isSelected = selectedIds?.has(entry.id);
            const isActionsOpen = openActionId === entry.id;
            const sourceMeta = sourceTypeMeta(entry.source_type);

            return (
              <tr
                key={entry.id}
                className={cn(
                  'hover:bg-muted/30 transition-colors',
                  entry.is_stale && 'bg-amber-50/50',
                  isSelected && 'bg-primary/5'
                )}
              >
                {onToggleSelect && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(entry.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                )}
                <td className="px-3 py-3">
                  <div
                    className={cn('cursor-pointer', onSelect && 'hover:text-primary')}
                    onClick={() => onSelect?.(entry)}
                  >
                    <div className="flex items-start gap-2">
                      <FileText
                        className={cn(
                          'h-4 w-4 mt-0.5 shrink-0',
                          entry.is_stale ? 'text-amber-600' : 'text-muted-foreground'
                        )}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium line-clamp-2" title={entry.question_text}>
                          {truncateText(entry.question_text, 100)}
                        </p>
                        <p
                          className="text-xs text-muted-foreground mt-0.5 line-clamp-1"
                          title={entry.response_text ?? undefined}
                        >
                          {truncateText(entry.response_text, 80)}
                        </p>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm text-muted-foreground">
                    {entry.category || '-'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span
                    className={cn(
                      'inline-flex px-1.5 py-0.5 rounded text-xs font-medium',
                      sourceMeta.colorClass
                    )}
                  >
                    {sourceMeta.label}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {isFirmSpecific(entry) ? (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      Firm
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Globe className="h-3 w-3" />
                      Global
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {entry.is_stale ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      Stale
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="h-3 w-3" />
                      Current
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(entry.updated_at)}
                  </span>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="relative inline-block">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setOpenActionId(isActionsOpen ? null : entry.id)}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>

                    {isActionsOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenActionId(null)}
                        />
                        <div className="absolute right-0 top-8 z-20 w-40 bg-background border rounded-md shadow-lg py-1">
                          <button
                            className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2"
                            onClick={() => {
                              setOpenActionId(null);
                              onEdit(entry);
                            }}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          {entry.is_stale ? (
                            <button
                              className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-green-600"
                              onClick={() => {
                                setOpenActionId(null);
                                onClearStale(entry);
                              }}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Mark Current
                            </button>
                          ) : (
                            <button
                              className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-amber-600"
                              onClick={() => {
                                setOpenActionId(null);
                                onMarkStale(entry);
                              }}
                            >
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Mark Stale
                            </button>
                          )}
                          <div className="border-t my-1" />
                          <button
                            className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-red-600"
                            onClick={() => {
                              setOpenActionId(null);
                              onDelete(entry);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {entries.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No entries found</p>
        </div>
      )}
    </div>
  );
}
